import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './HelpCenter.css';
import {
  NOTICES,
  PRESS_RELEASES,
  UPDATES,
  findHelpArticle,
  normalizeHelpTab,
  normalizeUpdateCategory,
} from '../../data/help';
import HelpHeader from './components/HelpHeader';
import HelpNewsBanner from './components/HelpNewsBanner';
import HelpTabs, { HELP_PANEL_ID, getHelpTabId } from './components/HelpTabs';
import { HelpArticleNotFound } from './components/HelpArticle';
import NoticeList from './components/NoticeList';
import NoticeDetail from './components/NoticeDetail';
import PressList from './components/PressList';
import PressDetail from './components/PressDetail';
import UpdateDetail from './components/UpdateDetail';
import UpdateList from './components/UpdateList';
import { getHelpListPath } from './components/helpFormat';
import { useAuth } from '../../contexts/AuthContext';
import { getWhatsNewItems, markWhatsNewSeen } from '../../config/whatsNew';

const PAGE_TITLE = '업데이트·공지사항 | 왓퀴즈 고객센터';
const PAGE_DESCRIPTION = '왓퀴즈의 새 기능 업데이트, 공지사항, 보도자료를 한곳에서 확인하세요.';

const stripHtml = (html = '') => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

// 목록에서 연 글이면 목록 주소(필터 포함)로 돌아가고, 바로 들어온 글이면 그 탭 목록으로 간다.
const getBackTo = (state, tab) => {
  const listSearch = state?.listSearch;
  if (typeof listSearch === 'string' && listSearch.startsWith('?')) return `/help${listSearch}`;
  return getHelpListPath(tab);
};

const DETAIL_VIEWS = {
  updates: UpdateDetail,
  notice: NoticeDetail,
  newsroom: PressDetail,
};

const HelpCenterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { noticeId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const article = noticeId ? findHelpArticle(noticeId) : null;
  const activeTab = article ? article.tab : normalizeHelpTab(searchParams.get('tab'));
  const activeCategory = activeTab === 'updates' ? normalizeUpdateCategory(searchParams.get('category')) : null;
  const linkState = { listSearch: location.search || `?tab=${activeTab}` };
  const uid = useAuth()?.currentUser?.uid;
  const articleId = article?.item.id;

  // 고객센터에서 업데이트를 읽었으면 새 소식 팝업·도트를 다시 띄우지 않는다 (목록은 최근 소식 전부, 글은 그 글만).
  useEffect(() => {
    if (!uid || activeTab !== 'updates') return;
    const recentIds = getWhatsNewItems().map(item => item.id);
    markWhatsNewSeen(uid, articleId ? recentIds.filter(id => id === articleId) : recentIds);
  }, [activeTab, articleId, uid]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    navigate(getHelpListPath(tab), { replace: true });
  };

  const handleCategoryChange = (category) => {
    if (category === activeCategory) return;
    const params = new URLSearchParams({ tab: 'updates' });
    if (category) params.set('category', category);
    navigate(`/help?${params.toString()}`, { replace: true });
  };

  if (noticeId) {
    const backTo = getBackTo(location.state, activeTab);
    if (!article) {
      return (
        <div className="help-center">
          <Helmet>
            <title>글을 찾을 수 없어요 | 왓퀴즈 고객센터</title>
            <meta name="robots" content="noindex" />
          </Helmet>
          <main className="help-center__main">
            <HelpArticleNotFound backTo={backTo} />
          </main>
        </div>
      );
    }
    const Detail = DETAIL_VIEWS[article.tab];
    const description = article.item.summary || stripHtml(article.item.content).slice(0, 120);
    return (
      <div className="help-center">
        <Helmet>
          <title>{`${article.item.title} | 왓퀴즈 고객센터`}</title>
          <meta name="description" content={description} />
        </Helmet>
        <main className="help-center__main">
          <Detail item={article.item} backTo={backTo} linkState={location.state} />
        </main>
      </div>
    );
  }

  return (
    <div className="help-center">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
      </Helmet>
      <main className="help-center__main">
        <HelpHeader />
        <HelpNewsBanner />
        <HelpTabs activeTab={activeTab} onChange={handleTabChange} />
        <section
          id={HELP_PANEL_ID}
          role="tabpanel"
          aria-labelledby={getHelpTabId(activeTab)}
          className="help-center__panel"
        >
          {activeTab === 'updates' && (
            <UpdateList
              items={UPDATES}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              linkState={linkState}
            />
          )}
          {activeTab === 'notice' && <NoticeList items={NOTICES} linkState={linkState} />}
          {activeTab === 'newsroom' && <PressList items={PRESS_RELEASES} linkState={linkState} />}
        </section>
      </main>
    </div>
  );
};

export default HelpCenterPage;
